import {
  Box,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  InputLabel,
  FormHelperText,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { useTranslation } from 'react-i18next'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm, SubmitHandler } from 'react-hook-form'
import { createVideoSchema } from './validations'
import { OrganizationType } from 'types/organization'
import { FileUpload } from 'components/FileUpload'

export type VideoFieldValues = {
  title: string
  description: string
  videoUrl: string
  ordinal: number
  audienceOrganizationType: OrganizationType
  instructionFileId: string
}

type Props = {
  isEditing?: boolean
  defaultValues: VideoFieldValues
  onClose: () => void
  onSubmit: SubmitHandler<VideoFieldValues>
}

export const VideoForm = ({
  isEditing,
  onClose,
  defaultValues,
  onSubmit,
}: Props) => {
  const { t } = useTranslation()
  const schema = createVideoSchema(t)

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<VideoFieldValues>({
    defaultValues,
    resolver: zodResolver(schema),
  })

  return (
    <Dialog
      PaperProps={{ sx: { minWidth: '300px' } }}
      open
      fullWidth
      onClose={onClose}
    >
      <DialogTitle>{isEditing ? t('editVideo') : t('createVideo')}</DialogTitle>
      <DialogCloseButton handleClose={onClose} />
      <DialogContent>
        <form>
          <Stack spacing={2}>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Box>
                  <TextField
                    {...field}
                    required
                    fullWidth
                    label={t('videoTitle')}
                    variant="outlined"
                    size="small"
                  />
                  {errors.title && (
                    <FormHelperText error>{errors.title?.message}</FormHelperText>
                  )}
                </Box>
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Box>
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    label={t('videoDescription')}
                    variant="outlined"
                  />
                  {errors.description && (
                    <FormHelperText error>
                      {errors.description?.message}
                    </FormHelperText>
                  )}
                </Box>
              )}
            />
            <Controller
              name="videoUrl"
              control={control}
              render={({ field }) => (
                <Box>
                  <TextField
                    {...field}
                    required
                    fullWidth
                    size="small"
                    label={t('videoUrl')}
                    variant="outlined"
                  />
                  {errors.videoUrl && (
                    <FormHelperText error>{errors.videoUrl?.message}</FormHelperText>
                  )}
                </Box>
              )}
            />
            <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
              <InputLabel required id="videoType">
                {t('videoType')}
              </InputLabel>
              <Controller
                name="audienceOrganizationType"
                control={control}
                rules={{ required: t('required') }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="videoType"
                    required
                    size="small"
                    label={t('videoType')}
                  >
                    {[
                      OrganizationType.CLIENT,
                      OrganizationType.TENDER_AUTHORITY,
                    ].map((type) => (
                      <MenuItem value={type} key={type}>
                        {t(`audienceOrganizationType.${type}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <Controller
              name="ordinal"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  size="small"
                  label={t('ordinal')}
                  variant="outlined"
                />
              )}
            />
            <Controller
              name="instructionFileId"
              control={control}
              render={({ field }) => (
                <Box>
                  <FileUpload
                    text={t('uploadInstruction')}
                    handleFile={(file) => field.onChange(file.id)}
                    fileId={field.value}
                  />

                  {errors.instructionFileId && (
                    <FormHelperText error>
                      {String(errors.instructionFileId?.message)}
                    </FormHelperText>
                  )}
                </Box>
              )}
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Stack width="100%" direction="row" justifyContent="space-between">
          <Button onClick={onClose} variant="outlined">
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          >
            {isEditing ? t('save') : t('add')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}
